/**
 * Aurora Atlas force-directed simulation — low damping + slow noise drift ("breath").
 * 디자인 simulation.js 를 TS 로 포팅.
 *
 * 좌표는 world 단위. 카메라 mapping 은 renderer 가 처리.
 */
import type { GraphEdgeData, GraphNodeData, SimEdge, SimNode } from './types.js';

export interface SimOptions {
  repulsion: number;
  springK: number;
  springLen: number;
  damping: number;
  gravity: number;
  breath: number;
}

const DEFAULT_OPTS: SimOptions = {
  repulsion: 2400,
  springK: 0.01,
  springLen: 110,
  damping: 0.86,
  gravity: 0.0006,
  breath: 0.18,
};

export class GraphSim {
  opts: SimOptions;
  nodes: SimNode[];
  edges: SimEdge[];
  byId: Record<string, SimNode>;
  cellSize = 80;

  constructor(nodes: GraphNodeData[], edges: GraphEdgeData[], opts: Partial<SimOptions> = {}) {
    this.opts = { ...DEFAULT_OPTS, ...opts };

    this.nodes = nodes.map((n, i) => {
      const angle = (i / Math.max(1, nodes.length)) * Math.PI * 2;
      const r = 200 + Math.random() * 600;
      return {
        ...n,
        x: Math.cos(angle) * r + (Math.random() - 0.5) * 60,
        y: Math.sin(angle) * r + (Math.random() - 0.5) * 60,
        vx: 0,
        vy: 0,
        nx: Math.random() * 1000,
        ny: Math.random() * 1000,
        degree: 0,
        radius: 3,
        pinned: false,
      } satisfies SimNode;
    });
    this.byId = Object.fromEntries(this.nodes.map((n) => [n.id, n]));

    this.edges = edges
      .map((e) => ({ source: this.byId[e.source], target: this.byId[e.target] }))
      .filter((e): e is SimEdge => Boolean(e.source && e.target));

    for (const e of this.edges) {
      e.source.degree++;
      e.target.degree++;
    }
    for (const n of this.nodes) {
      // log scale + 최대 cap — IT/개발 허브 같은 high-degree 노드가 비정상적으로 커지는 것 방지.
      n.radius = Math.min(11, 2.6 + Math.log2(1 + n.degree) * 1.6);
      if (n.isCenter) n.radius = Math.min(15, n.radius * 1.4);
      else if (n.isCategoryHub) n.radius = Math.min(13, n.radius * 1.2);
    }
  }

  step(dt = 1): void {
    const N = this.nodes;
    const opts = this.opts;
    const t = performance.now() / 1000;

    for (const n of N) {
      n._fx = 0;
      n._fy = 0;
    }

    // 공간 grid 로 repulsion (Barnes-Hut-lite)
    const grid = new Map<string, SimNode[]>();
    const cs = this.cellSize;
    for (const n of N) {
      const gx = Math.floor(n.x / cs);
      const gy = Math.floor(n.y / cs);
      const key = `${gx},${gy}`;
      let cell = grid.get(key);
      if (!cell) {
        cell = [];
        grid.set(key, cell);
      }
      cell.push(n);
    }
    for (const n of N) {
      const gx = Math.floor(n.x / cs);
      const gy = Math.floor(n.y / cs);
      for (let ix = -1; ix <= 1; ix++) {
        for (let iy = -1; iy <= 1; iy++) {
          const cell = grid.get(`${gx + ix},${gy + iy}`);
          if (!cell) continue;
          for (const m of cell) {
            if (m === n) continue;
            const dx = n.x - m.x;
            const dy = n.y - m.y;
            const d2 = dx * dx + dy * dy + 0.01;
            if (d2 > 16000) continue;
            const f = opts.repulsion / d2;
            const d = Math.sqrt(d2);
            n._fx = (n._fx ?? 0) + (dx / d) * f;
            n._fy = (n._fy ?? 0) + (dy / d) * f;
          }
        }
      }
    }

    // springs
    for (const e of this.edges) {
      const dx = e.target.x - e.source.x;
      const dy = e.target.y - e.source.y;
      const d = Math.sqrt(dx * dx + dy * dy) + 0.001;
      const f = (d - opts.springLen) * opts.springK;
      const fx = (dx / d) * f;
      const fy = (dy / d) * f;
      e.source._fx = (e.source._fx ?? 0) + fx;
      e.source._fy = (e.source._fy ?? 0) + fy;
      e.target._fx = (e.target._fx ?? 0) - fx;
      e.target._fy = (e.target._fy ?? 0) - fy;
    }

    // integrate + breath + gravity + damping
    for (const n of N) {
      n._fx = (n._fx ?? 0) - n.x * opts.gravity;
      n._fy = (n._fy ?? 0) - n.y * opts.gravity;

      n._fx += Math.sin(t * 0.32 + n.nx) * opts.breath;
      n._fy += Math.cos(t * 0.27 + n.ny) * opts.breath;

      if (n.pinned) {
        n.vx = 0;
        n.vy = 0;
        continue;
      }

      n.vx = (n.vx + (n._fx ?? 0) * dt) * opts.damping;
      n.vy = (n.vy + (n._fy ?? 0) * dt) * opts.damping;

      const v2 = n.vx * n.vx + n.vy * n.vy;
      const VMAX = 9;
      if (v2 > VMAX * VMAX) {
        const s = VMAX / Math.sqrt(v2);
        n.vx *= s;
        n.vy *= s;
      }
      n.x += n.vx * dt;
      n.y += n.vy * dt;
    }
  }
}
